/* eslint complexity: ["error", {max: 9}] -- isFieldEmpty covers different form field types */
import { isNil } from "lodash";

import { FormFieldType } from "@/ui/composed/form/types";
import type {
  FormFieldConfig,
  FormFieldProps,
  FormFieldTypeValue,
  FormValidationResult,
} from "@/ui/composed/form/types";

const isAddressEmpty = (value: GooglePlacesAddress): boolean => {
  return (
    !value.addressLine1 ||
    value.addressLine1.trim() === "" ||
    !value.city ||
    value.city.trim() === "" ||
    !value.state ||
    value.state.trim() === "" ||
    !value.zip ||
    value.zip.trim() === ""
  );
};

/**
 * Determines if a form field value is empty based on its field type
 *
 * @param value - The field value to check
 * @param type - The type of form field (Text, CheckBox, Address, etc.)
 * @returns true if the field is considered empty, false otherwise
 *
 * @example
 * isFieldEmpty({ value: "", type: FormFieldType.Text }) // true
 * isFieldEmpty({ value: "hello", type: FormFieldType.Text }) // false
 * isFieldEmpty({ value: false, type: FormFieldType.CheckBox }) // true (unchecked required checkbox)
 * isFieldEmpty({ value: true, type: FormFieldType.CheckBox }) // false (checked)
 * isFieldEmpty({ value: undefined, type: FormFieldType.Text }) // true
 */
const isFieldEmpty = ({ type, value }: FormFieldTypeValue): boolean => {
  if (type === FormFieldType.Address) {
    return !value || isAddressEmpty(value);
  }
  if (type === FormFieldType.CheckBox) {
    // for checkboxes, only checked (true) is considered "filled"
    return value !== true;
  }
  if (type === FormFieldType.Date) {
    return !value;
  }
  // Select or Text fields are considered empty if the value is nil or an empty string
  return isNil(value) || value.trim() === "";
};

/**
 * Validates form values against validation schema
 * This is the same logic used internally by FormFieldGroup
 *
 * @param params - Validation parameters
 * @param params.values - Current field values
 * @param params.validationSchema - Record of field configurations indexed by field name
 * @returns FormValidationResult with isValid flag and error map
 *
 * @example
 * const result = validateFields({
 *   values,
 *   validationSchema: {
 *     email: { isRequired: true, type: FormFieldType.Text },
 *     phone: { isRequired: false, type: FormFieldType.Text },
 *   },
 * });
 * if (result.isValid) {
 *   // All required fields are filled
 * }
 */
const validateFields = <T extends string>({
  validationSchema,
  values,
}: {
  validationSchema: Record<T, FormFieldConfig>;
  values: Record<string, FormFieldProps["value"]>;
}): FormValidationResult => {
  const errors: Record<string, boolean> = {};
  let isValid = true;

  Object.entries<FormFieldConfig>(validationSchema).forEach(
    ([name, config]) => {
      if (!config.isRequired || config.isHidden) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const isEmpty = isFieldEmpty({
        type: config.type,
        value: values[name],
      } as FormFieldTypeValue);

      if (isEmpty) {
        errors[name] = true;
        isValid = false;
      }
    },
  );

  return { errors, isValid };
};

export { isFieldEmpty, validateFields };
